import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Alert,
  TextField,
  LinearProgress,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  ArrowDownTrayIcon,
  CameraIcon,
  DocumentTextIcon,
  RectangleStackIcon,
  XMarkIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import imageCompression from 'browser-image-compression';
import Modal from '../Modal';
import { AppIconButton } from '../ui';
import type { ThemeColors } from '../../context/ThemeContext';
import { useImageUpload } from '../../hooks/useImageUpload';
import { downloadBlob } from '../../utils/downloadBlob';
import {
  sheetsApi,
  type SheetImportResponse,
  type SheetRow,
  type SheetStatus,
  type SheetType,
  type WeekDay,
} from '../../api/inventoryApi';

interface WeeklySheetsTabProps {
  theme: ThemeColors;
  vendorId?: number;
}

const DAYS: WeekDay[] = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const LOW_CONFIDENCE = 0.7;

// --- Week helpers (Monday-anchored, local-date safe) ---

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Monday of the week containing d (local time). */
function mondayOf(d: Date): Date {
  const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = date.getDay(); // 0=Sun .. 6=Sat
  const shift = (dow === 0 ? -6 : 1) - dow;
  date.setDate(date.getDate() + shift);
  return date;
}

function currentMondayIso(): string {
  return isoDate(mondayOf(new Date()));
}

/** Human range label, e.g. "6–12 Jul 2026". */
function weekRangeLabel(mondayIso: string): string {
  const [y, m, d] = mondayIso.split('-').map(Number);
  const start = new Date(y, m - 1, d);
  const end = new Date(y, m - 1, d + 6);
  const startDay = start.getDate();
  const endDay = end.getDate();
  const month = end.toLocaleDateString(undefined, { month: 'short' });
  const startMonth = start.toLocaleDateString(undefined, { month: 'short' });
  const year = end.getFullYear();
  return startMonth === month
    ? `${startDay}–${endDay} ${month} ${year}`
    : `${startDay} ${startMonth} – ${endDay} ${month} ${year}`;
}

function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'sheet';
}

async function fileToCompressedBase64(
  file: File,
): Promise<{ base64: string; mimeType: string; dataUrl: string }> {
  // Higher than the app's usual 1200px: the vision model reads handwritten
  // digits and a small QR from this photo, both of which die at low res.
  const compressed = await imageCompression(file, {
    maxSizeMB: 1.2,
    maxWidthOrHeight: 2400,
    useWebWorker: true,
    fileType: 'image/jpeg',
  });
  const dataUrl = await imageCompression.getDataUrlFromFile(compressed);
  return {
    base64: dataUrl.split(',')[1] || '',
    mimeType: compressed.type || 'image/jpeg',
    dataUrl,
  };
}

const STATUS_LABEL: Record<SheetStatus, string> = {
  DRAFT: 'Draft',
  CONFIRMED: 'Confirmed',
  CANCELLED: 'Cancelled',
};

// --- Component ---

const WeeklySheetsTab: React.FC<WeeklySheetsTabProps> = ({ theme, vendorId }) => {
  const { uploadImage } = useImageUpload();

  const [categories, setCategories] = useState<string[]>([]);
  const [weekStart, setWeekStart] = useState<string>(currentMondayIso());
  const [downloading, setDownloading] = useState<string | null>(null);

  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Review dialog
  const [draft, setDraft] = useState<SheetImportResponse | null>(null);
  const [reviewRows, setReviewRows] = useState<SheetRow[]>([]);
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const [history, setHistory] = useState<SheetImportResponse[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await sheetsApi.getCategories();
      setCategories(res.data || []);
    } catch {
      // categories are non-critical; the sales sheet still works without them
      setCategories([]);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      const res = await sheetsApi.getHistory(20);
      setHistory(res.data || []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
    fetchHistory();
  }, [fetchCategories, fetchHistory]);

  // --- Template downloads ---

  const handleDownload = useCallback(
    async (type: SheetType, category?: string) => {
      const key = `${type}:${category ?? ''}`;
      setError(null);
      try {
        setDownloading(key);
        const res = await sheetsApi.getTemplate({ type, category, weekStart });
        const name =
          type === 'STOCK'
            ? `stock-sheet-${slug(category || 'all')}-${weekStart}.pdf`
            : `sales-sheet-${weekStart}.pdf`;
        downloadBlob(res.data as Blob, name);
      } catch {
        setError('Could not generate the PDF. Please try again.');
      } finally {
        setDownloading(null);
      }
    },
    [weekStart],
  );

  // --- Import filled sheet ---

  const openReview = useCallback((sheet: SheetImportResponse) => {
    setDraft(sheet);
    setReviewRows((sheet.rows || []).map((r) => ({ ...r, days: { ...r.days } })));
  }, []);

  const handleFileSelected = useCallback(
    async (file: File) => {
      setError(null);
      setSuccess(null);
      if (!file.type.startsWith('image/')) {
        setError('Please select an image of the filled sheet.');
        return;
      }
      setImporting(true);
      try {
        const { base64, mimeType, dataUrl } = await fileToCompressedBase64(file);
        setPreview(dataUrl);

        // Best-effort: also archive the original photo to the image gateway.
        // Never block the import on this.
        let imageKey: string | undefined;
        if (vendorId) {
          try {
            const k = await uploadImage(file, vendorId, 'stock-sheet', Date.now());
            imageKey = k || undefined;
          } catch {
            imageKey = undefined;
          }
        }

        const res = await sheetsApi.importSheet({
          imageBase64: base64,
          imageMimeType: mimeType,
          imageKey,
          weekStartHint: weekStart,
        });
        openReview(res.data);
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 409) {
          setError('This week has already been confirmed for this sheet. Cancel or review the existing entry.');
        } else if (status === 422) {
          setError("Couldn't identify this sheet. Make sure the whole page is visible, well-lit, and not blurry, then retake.");
        } else if (status === 403) {
          setError('This sheet belongs to a different venue. Please use a sheet printed for your account.');
        } else {
          setError('Import failed. Please retake the photo and try again.');
        }
        setPreview(null);
      } finally {
        setImporting(false);
      }
    },
    [weekStart, vendorId, uploadImage, openReview],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFileSelected(file);
      e.target.value = '';
    },
    [handleFileSelected],
  );

  // --- Review edits ---

  const updateDay = useCallback((rowIdx: number, day: WeekDay, raw: string) => {
    const value = raw === '' ? null : parseFloat(raw);
    setReviewRows((prev) =>
      prev.map((r, i) =>
        i === rowIdx ? { ...r, days: { ...r.days, [day]: Number.isNaN(value) ? null : value } } : r,
      ),
    );
  }, []);

  const updateWeekEnd = useCallback((rowIdx: number, raw: string) => {
    const value = raw === '' ? null : parseFloat(raw);
    setReviewRows((prev) =>
      prev.map((r, i) => (i === rowIdx ? { ...r, weekEndCount: Number.isNaN(value) ? null : value } : r)),
    );
  }, []);

  const closeReview = useCallback(() => {
    setDraft(null);
    setReviewRows([]);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!draft) return;
    setConfirming(true);
    setError(null);
    try {
      const res = await sheetsApi.confirmSheet(draft.id, reviewRows);
      const s = res.data;
      const parts: string[] = [];
      if (s.receiptsCreated) parts.push(`${s.receiptsCreated} stock receipt${s.receiptsCreated === 1 ? '' : 's'}`);
      if (s.countAdjustments) parts.push(`${s.countAdjustments} count adjustment${s.countAdjustments === 1 ? '' : 's'}`);
      if (s.salesDaysUpserted) parts.push(`${s.salesDaysUpserted} sales day${s.salesDaysUpserted === 1 ? '' : 's'}`);
      const warningSuffix = s.warnings?.length ? ` ${s.warnings.join(' ')}` : '';
      setSuccess((parts.length ? `Saved: ${parts.join(', ')}.` : 'Sheet saved.') + warningSuffix);
      closeReview();
      setPreview(null);
      fetchHistory();
    } catch (err) {
      const resp = (err as { response?: { status?: number; data?: { message?: string } } })?.response;
      if (resp?.status === 409) {
        setError('This week was already confirmed elsewhere.');
      } else if (resp?.status === 422 && resp.data?.message) {
        setError(resp.data.message);
      } else {
        setError('Could not save the sheet. Please try again.');
      }
    } finally {
      setConfirming(false);
    }
  }, [draft, reviewRows, closeReview, fetchHistory]);

  const handleCancelDraft = useCallback(async () => {
    if (!draft) return;
    setCancelling(true);
    try {
      await sheetsApi.cancelSheet(draft.id);
      closeReview();
      setPreview(null);
      fetchHistory();
    } catch {
      setError('Could not cancel the draft.');
    } finally {
      setCancelling(false);
    }
  }, [draft, closeReview, fetchHistory]);

  const handleReopen = useCallback(
    async (id: number) => {
      setError(null);
      try {
        const res = await sheetsApi.getSheet(id);
        openReview(res.data);
      } catch {
        setError('Could not open this sheet.');
      }
    },
    [openReview],
  );

  // --- Styles ---

  const cardSx = {
    backgroundColor: theme.components?.card?.background,
    border: `1px solid ${theme.components?.card?.border}`,
    borderRadius: 2,
  };

  const statusChipSx = (status: SheetStatus) => {
    const map: Record<SheetStatus, string> = {
      DRAFT: theme.warning || '#d97706',
      CONFIRMED: theme.success || '#10b981',
      CANCELLED: theme.gray?.[500] || '#6b7280',
    };
    const c = map[status];
    return { backgroundColor: `${c}20`, color: c, fontWeight: 600, fontSize: '0.7rem' };
  };

  const isStock = draft?.sheetType === 'STOCK';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* ===== Download templates ===== */}
      <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 3 } }}>
        <Typography sx={{ fontWeight: 700, color: theme.text, fontSize: '1rem', mb: 0.5 }}>
          Download weekly sheets
        </Typography>
        <Typography variant="body2" sx={{ color: theme.gray?.[600], mb: 2 }}>
          Print a stock sheet per category and an offline-sales sheet. Staff fill in daily quantities by hand.
        </Typography>

        {/* Week picker (snaps to Monday) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2.5 }}>
          <TextField
            label="Week starting (Monday)"
            type="date"
            size="small"
            value={weekStart}
            onChange={(e) => {
              if (!e.target.value) return;
              const [y, m, d] = e.target.value.split('-').map(Number);
              setWeekStart(isoDate(mondayOf(new Date(y, m - 1, d))));
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'data-testid': 'sheet-week-start' }}
            sx={{ width: 200 }}
          />
          <Chip
            label={weekRangeLabel(weekStart)}
            size="small"
            sx={{ backgroundColor: `${theme.primary}18`, color: theme.primary, fontWeight: 600 }}
          />
        </Box>

        {/* Category cards + offline sales */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 1.5,
          }}
        >
          {categories.map((cat) => {
            const key = `STOCK:${cat}`;
            return (
              <Box
                key={cat}
                sx={{
                  ...cardSx,
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <RectangleStackIcon style={{ width: 20, height: 20, color: theme.primary, flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={{ fontWeight: 600, color: theme.text, fontSize: '0.875rem' }}>
                      {cat}
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.gray?.[600] }}>Stock sheet</Typography>
                  </Box>
                </Box>
                <AppIconButton
                  tooltip={`Download ${cat} stock sheet`}
                  variant="primary"
                  size="small"
                  disabled={downloading === key}
                  onClick={() => handleDownload('STOCK', cat)}
                  data-testid={`download-stock-${slug(cat)}`}
                  icon={
                    downloading === key ? (
                      <CircularProgress size={16} sx={{ color: theme.primary }} />
                    ) : (
                      <ArrowDownTrayIcon style={{ width: 18, height: 18 }} />
                    )
                  }
                />
              </Box>
            );
          })}

          {/* Offline sales sheet */}
          <Box
            sx={{
              ...cardSx,
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              borderColor: `${theme.accent}66`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <DocumentTextIcon style={{ width: 20, height: 20, color: theme.accent, flexShrink: 0 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography noWrap sx={{ fontWeight: 600, color: theme.text, fontSize: '0.875rem' }}>
                  Offline Sales
                </Typography>
                <Typography variant="caption" sx={{ color: theme.gray?.[600] }}>Qty sold per day</Typography>
              </Box>
            </Box>
            <AppIconButton
              tooltip="Download offline sales sheet"
              variant="primary"
              size="small"
              disabled={downloading === 'SALES:'}
              onClick={() => handleDownload('SALES')}
              data-testid="download-sales-sheet"
              icon={
                downloading === 'SALES:' ? (
                  <CircularProgress size={16} sx={{ color: theme.primary }} />
                ) : (
                  <ArrowDownTrayIcon style={{ width: 18, height: 18 }} />
                )
              }
            />
          </Box>
        </Box>

        {categories.length === 0 && (
          <Typography variant="caption" sx={{ color: theme.gray?.[500], display: 'block', mt: 1.5 }}>
            No inventory categories yet. Add a category to an item to get a stock sheet for it.
          </Typography>
        )}
      </Paper>

      {/* ===== Import filled sheet ===== */}
      <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 3 } }}>
        <Typography sx={{ fontWeight: 700, color: theme.text, fontSize: '1rem', mb: 0.5 }}>
          Import a filled sheet
        </Typography>
        <Typography variant="body2" sx={{ color: theme.gray?.[600], mb: 2 }}>
          Snap a photo of the hand-filled sheet. We read the numbers so you can review and confirm.
        </Typography>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />

        {importing ? (
          <Box>
            {preview && (
              <Box
                component="img"
                src={preview}
                alt="Sheet preview"
                sx={{
                  width: '100%',
                  maxHeight: 200,
                  objectFit: 'contain',
                  borderRadius: '10px',
                  border: `1px solid ${theme.components?.card?.border}`,
                  mb: 1.5,
                  backgroundColor: theme.gray?.[100],
                }}
              />
            )}
            <LinearProgress
              sx={{
                borderRadius: 4,
                backgroundColor: `${theme.primary}22`,
                '& .MuiLinearProgress-bar': { backgroundColor: theme.primary },
              }}
            />
            <Typography variant="caption" sx={{ color: theme.gray?.[600], display: 'block', mt: 1 }}>
              Reading the sheet — this can take a few seconds…
            </Typography>
          </Box>
        ) : (
          <Button
            variant="contained"
            startIcon={<CameraIcon style={{ width: 20, height: 20 }} />}
            onClick={() => fileInputRef.current?.click()}
            data-testid="import-sheet-btn"
            sx={{
              backgroundColor: `${theme.button?.primary?.bg || theme.primary} !important`,
              color: `${theme.button?.primary?.text || '#fff'} !important`,
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '10px',
              py: 1.25,
              px: 3,
              boxShadow: 'none !important',
              '&:hover': { backgroundColor: `${theme.button?.primary?.hover || theme.primary} !important`, boxShadow: 'none !important' },
            }}
          >
            Take / upload photo
          </Button>
        )}
      </Paper>

      {/* ===== History ===== */}
      <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography sx={{ fontWeight: 700, color: theme.text, fontSize: '1rem' }}>Recent imports</Typography>
          <AppIconButton
            tooltip="Refresh"
            variant="primary"
            size="small"
            disabled={historyLoading}
            onClick={fetchHistory}
            icon={<ArrowPathIcon className={`h-4 w-4 ${historyLoading ? 'animate-spin' : ''}`} />}
            data-testid="sheets-history-refresh"
          />
        </Box>

        {historyLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={32} /></Box>
        ) : history.length === 0 ? (
          <Typography variant="body2" sx={{ color: theme.gray?.[500], py: 2, textAlign: 'center' }}>
            No sheets imported yet.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {history.map((h) => {
              const clickable = h.status === 'DRAFT';
              return (
                <Box
                  key={h.id}
                  onClick={clickable ? () => handleReopen(h.id) : undefined}
                  data-testid={`sheet-history-${h.id}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    p: 1.25,
                    borderRadius: '8px',
                    border: `1px solid ${theme.components?.card?.border}`,
                    cursor: clickable ? 'pointer' : 'default',
                    '&:hover': clickable ? { backgroundColor: `${theme.primary}0c` } : {},
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, color: theme.text, fontSize: '0.875rem' }}>
                      {h.sheetType === 'STOCK' ? (h.category || 'Stock') : 'Offline Sales'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.gray?.[600] }}>
                      Week of {weekRangeLabel(h.weekStart)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label={h.sheetType === 'STOCK' ? 'Stock' : 'Sales'} size="small"
                      sx={{ backgroundColor: `${theme.gray?.[200]}66`, color: theme.gray?.[600], fontSize: '0.7rem' }} />
                    <Chip label={STATUS_LABEL[h.status]} size="small" sx={statusChipSx(h.status)} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>

      {/* ===== Review dialog ===== */}
      <Modal
        isOpen={draft !== null}
        onClose={closeReview}
        title="Review extracted sheet"
        maxWidth="lg"
      >
        {draft && (
          <Box sx={{ pb: 1 }}>
            {/* Meta */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
              <Chip
                label={isStock ? 'Stock sheet' : 'Offline sales'}
                size="small"
                sx={{ backgroundColor: `${theme.primary}18`, color: theme.primary, fontWeight: 600 }}
              />
              {draft.category && (
                <Chip label={draft.category} size="small"
                  sx={{ backgroundColor: `${theme.gray?.[200]}66`, color: theme.gray?.[700], fontWeight: 600 }} />
              )}
              <Chip label={`Week of ${weekRangeLabel(draft.weekStart)}`} size="small"
                sx={{ backgroundColor: `${theme.accent}18`, color: theme.accent, fontWeight: 600 }} />
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
              Check the numbers below against the paper sheet. Cells we were unsure of are highlighted — edit any that are wrong before confirming.
            </Alert>

            {reviewRows.length === 0 ? (
              <Typography variant="body2" sx={{ color: theme.gray?.[500], py: 3, textAlign: 'center' }}>
                No rows were extracted from this sheet.
              </Typography>
            ) : (
              <TableContainer
                sx={{
                  maxHeight: '55vh',
                  border: `1px solid ${theme.components?.card?.border}`,
                  borderRadius: '10px',
                }}
              >
                <Table size="small" stickyHeader sx={{ minWidth: isStock ? 720 : 620 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          color: theme.text,
                          backgroundColor: theme.components?.card?.background,
                          position: 'sticky',
                          left: 0,
                          zIndex: 3,
                          minWidth: 140,
                        }}
                      >
                        Item
                      </TableCell>
                      {DAYS.map((d) => (
                        <TableCell key={d} align="center" sx={{ fontWeight: 700, color: theme.text, backgroundColor: theme.components?.card?.background, minWidth: 60 }}>
                          {d}
                        </TableCell>
                      ))}
                      {isStock && (
                        <TableCell align="center" sx={{ fontWeight: 700, color: theme.text, backgroundColor: theme.components?.card?.background, minWidth: 90 }}>
                          Week-end
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reviewRows.map((row, idx) => {
                      const low = row.confidence < LOW_CONFIDENCE;
                      return (
                        <TableRow key={`${row.itemId ?? row.menuItemId ?? 'x'}-${row.portionId ?? ''}-${idx}`}>
                          <TableCell
                            sx={{
                              position: 'sticky',
                              left: 0,
                              zIndex: 2,
                              backgroundColor: theme.components?.card?.background,
                              borderRight: `1px solid ${theme.components?.card?.border}`,
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 600, color: theme.text }}>
                              {row.itemName}
                              {row.portionName ? ` · ${row.portionName}` : ''}
                            </Typography>
                            {row.unit && (
                              <Typography variant="caption" sx={{ color: theme.gray?.[500] }}>{row.unit}</Typography>
                            )}
                            {row.warnings?.length > 0 && (
                              <Typography variant="caption" sx={{ color: theme.warning || '#d97706', display: 'block' }}>
                                {row.warnings.join(', ')}
                              </Typography>
                            )}
                          </TableCell>
                          {DAYS.map((d) => (
                            <TableCell key={d} align="center" sx={{ p: 0.5 }}>
                              <TextField
                                value={row.days[d] ?? ''}
                                onChange={(e) => updateDay(idx, d, e.target.value)}
                                type="number"
                                size="small"
                                variant="outlined"
                                inputProps={{
                                  inputMode: 'decimal',
                                  step: 0.01,
                                  style: { textAlign: 'center', padding: '8px 4px', minHeight: 24 },
                                  'aria-label': `${row.itemName} ${d}`,
                                }}
                                sx={{
                                  width: 56,
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    backgroundColor: low ? `${theme.warning || '#d97706'}12` : undefined,
                                    '& fieldset': low ? { borderColor: `${theme.warning || '#d97706'}88` } : undefined,
                                  },
                                }}
                              />
                            </TableCell>
                          ))}
                          {isStock && (
                            <TableCell align="center" sx={{ p: 0.5 }}>
                              <TextField
                                value={row.weekEndCount ?? ''}
                                onChange={(e) => updateWeekEnd(idx, e.target.value)}
                                type="number"
                                size="small"
                                variant="outlined"
                                inputProps={{
                                  inputMode: 'decimal',
                                  step: 0.01,
                                  style: { textAlign: 'center', padding: '8px 4px', minHeight: 24 },
                                  'aria-label': `${row.itemName} week-end count`,
                                }}
                                sx={{
                                  width: 72,
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    backgroundColor: low ? `${theme.warning || '#d97706'}12` : undefined,
                                    '& fieldset': low ? { borderColor: `${theme.warning || '#d97706'}88` } : undefined,
                                  },
                                }}
                              />
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Import-time notices (e.g. sheet identified from header, not QR) */}
            {draft.warnings && draft.warnings.length > 0 && (
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: '10px',
                  backgroundColor: `${theme.warning || '#d97706'}10`,
                  border: `1px solid ${theme.warning || '#d97706'}44`,
                }}
              >
                {draft.warnings.map((w, i) => (
                  <Typography key={i} variant="caption" sx={{ color: theme.text, display: 'block' }}>
                    • {w}
                  </Typography>
                ))}
              </Box>
            )}

            {/* Unmatched rows — informational */}
            {draft.unmatchedRows && draft.unmatchedRows.length > 0 && (
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: '10px',
                  backgroundColor: `${theme.warning || '#d97706'}10`,
                  border: `1px solid ${theme.warning || '#d97706'}44`,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.warning || '#d97706', mb: 0.5 }}>
                  Rows we couldn't match ({draft.unmatchedRows.length})
                </Typography>
                <Typography variant="caption" sx={{ color: theme.gray?.[600], display: 'block', mb: 1 }}>
                  These lines didn't match a known item and won't be saved. Add or rename the item, then re-import.
                </Typography>
                {draft.unmatchedRows.map((u, i) => (
                  <Typography key={i} variant="caption" sx={{ color: theme.text, display: 'block' }}>
                    • {u.label}: {DAYS.map((d) => u.days[d] ?? '–').join(' / ')}
                  </Typography>
                ))}
              </Box>
            )}

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', mt: 3, pt: 2, borderTop: `1px solid ${theme.components?.card?.border}` }}>
              <Button
                onClick={handleCancelDraft}
                disabled={cancelling || confirming}
                startIcon={cancelling ? <CircularProgress size={16} /> : <XMarkIcon style={{ width: 16, height: 16 }} />}
                sx={{ color: theme.danger || '#dc2626', textTransform: 'none', fontWeight: 600 }}
                data-testid="sheet-cancel-btn"
              >
                Discard draft
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirm}
                disabled={confirming || cancelling || reviewRows.length === 0}
                data-testid="sheet-confirm-btn"
                sx={{
                  backgroundColor: `${theme.button?.primary?.bg || theme.primary} !important`,
                  color: `${theme.button?.primary?.text || '#fff'} !important`,
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '10px',
                  px: 3,
                  boxShadow: 'none !important',
                  '&:hover': { backgroundColor: `${theme.button?.primary?.hover || theme.primary} !important`, boxShadow: 'none !important' },
                  '&:disabled': { opacity: 0.5 },
                }}
              >
                {confirming ? 'Saving…' : 'Confirm & save'}
              </Button>
            </Box>
          </Box>
        )}
      </Modal>
    </Box>
  );
};

export default WeeklySheetsTab;
